/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Routes home-screen widget taps (`happy-app://widget/<target>`) to the matching
 * in-app screen. Navigation may not be ready on a cold start (the tap launches
 * the app), so we retry a few times until `navigationRef` is mounted. Unknown
 * targets are ignored — the app still opens, just at its default screen.
 */

/* global setTimeout */
import { navigateWhenReady } from '../navigationRef';
import { WIDGET_DEEPLINK_SCHEME } from '../../config/widgetConfig';

/**
 * Widget target → in-app route resolver. Each resolver receives the deep-link's
 * query params (e.g. `messageId`, `id`) and returns `{ route, params }` for the
 * specific record, or `{ route: null }` to just open the app at its default
 * screen. This lets a widget tap open the exact mail thread / work order / order
 * instead of only the module landing screen.
 * @type {Record<string, (params: Record<string,string>) => { route: string|null, params?: object }>}
 */
const TARGET_ROUTES = {
    // The unified calendar is a tab of the management home screen (same nested route admin links use).
    calendar: () => ({ route: 'ManagementHomeScreen', params: { screen: 'ManagementCalendar' } }),
    // One calendar event: the calendar tab jumps to the day and opens the event once (`focusEventId` / `focusDate`).
    event: (params) => (params.id
        ? {
            route: 'ManagementHomeScreen',
            params: {
                screen: 'ManagementCalendar',
                params: { focusEventId: params.id, ...(params.date ? { focusDate: params.date } : {}) },
            },
        }
        : { route: 'ManagementHomeScreen', params: { screen: 'ManagementCalendar' } }),
    stats: () => ({ route: 'StatisticsScreen' }),
    // A specific message opens the thread; otherwise the mailbox.
    mail: (params) => (params.messageId
        ? { route: 'MailThreadScreen', params: { messageId: params.messageId, threadKey: params.threadKey || undefined } }
        : { route: 'MailHomeScreen' }),
    // Service work-order timer → the work-order (worksheet) detail, which
    // self-fetches from `worksheetId`; falls back to the list.
    worksheet: (params) => (params.id
        ? { route: 'WorkSheetDetailScreen', params: { worksheetId: params.id } }
        : { route: 'WorkSheetListScreen' }),
    // A pending-order row → that order. `OrderDetailsScreen` resolves `{ orderId }` against the live order feed (the
    // same params an order push uses, see `notificationNavigation`); without an id the self-loading list is the landing.
    order: (params) => (params.id
        ? { route: 'OrderDetailsScreen', params: { orderId: params.id } }
        : { route: 'OrderListScreen' }),
    // A project → the shared management record detail (self-fetches by id via the by-id loader); no id → the hub.
    project: (params) => (params.id
        ? { route: 'ManagementDetailScreen', params: { type: 'projects', id: params.id } }
        : { route: 'ManagementHomeScreen' }),
    // One task row → that task's detail; the module header / no id → the tasks list.
    task: (params) => (params.id
        ? { route: 'ManagementDetailScreen', params: { type: 'tasks', id: params.id } }
        : { route: 'ManagementListScreen', params: { type: 'tasks' } }),
    tasks: (params) => (params.id
        ? { route: 'ManagementDetailScreen', params: { type: 'tasks', id: params.id } }
        : { route: 'ManagementListScreen', params: { type: 'tasks' } }),
    // A quote (insight list rows, e.g. expiring quotes) → its detail; no id → the quotes list.
    quote: (params) => (params.id
        ? { route: 'ManagementDetailScreen', params: { type: 'quotes', id: params.id } }
        : { route: 'ManagementListScreen', params: { type: 'quotes' } }),
    // A NAV invoice (`dir=in` payable / default `out` receivable) → its detail; no id → the matching list.
    invoice: (params) => {
        const type = params.dir === 'in' ? 'invoicesInbound' : 'invoicesOutbound';

        return params.id
            ? { route: 'ManagementDetailScreen', params: { type, id: params.id } }
            : { route: 'ManagementListScreen', params: { type } };
    },
    // A support ticket → its detail; no id → the tickets list.
    ticket: (params) => (params.id
        ? { route: 'ManagementDetailScreen', params: { type: 'supportTickets', id: params.id } }
        : { route: 'ManagementListScreen', params: { type: 'supportTickets' } }),
    // Mail composer (the mail widget's "new message" chip).
    compose: () => ({ route: 'MailComposeScreen' }),
    // Time tracker. `compose=1` (the widget's "+ New task" row) tells the screen
    // to open its project/task picker on arrival, so naming a brand-new task is
    // the first thing the user can do — a home-screen widget can never take text
    // input itself, so this deep link IS the "start something not in the list"
    // affordance. Without the flag the tap just opens the tracker.
    timer: (params) => ({
        route: 'TimeTrackerScreen',
        params: params.compose ? { compose: params.compose } : undefined,
    }),
    // Meeting-recorder widget → the unified recording settings screen.
    meeting: () => ({ route: 'MeetingRecordingSettingsScreen' }),
    // Team Chat widget: a conversation row opens that channel (same params as
    // a chat push), the card opens the chat hub.
    chat: (params) => (params.channelId
        ? { route: 'ChatChannelScreen', params: { channelId: params.channelId } }
        : { route: 'ChatHomeScreen', params: { tab: 'chats' } }),
    // Meetings widget: "Join" on a LIVE meeting opens the meeting room; every
    // other tap lands on the hub's meetings tab. A scheduled meeting is never
    // opened directly — opening one as staff starts it.
    meetings: (params) => (params.meetingId
        ? { route: 'ChatMeetingScreen', params: { meetingId: params.meetingId } }
        : { route: 'ChatHomeScreen', params: { tab: 'meetings' } }),
    // Calls widget: the call history, or the dialer pre-filled with a recent
    // contact's number. The user still presses call — a widget tap never dials.
    calls: () => ({ route: 'CallHistoryScreen' }),
    dial: (params) => ({
        route: 'DialerScreen',
        params: params.number ? { number: params.number } : undefined,
    }),
    home: () => ({ route: null }),
    // Insight widgets (finance / bank / sales / operations). Each target maps to
    // the nearest REAL registered screen; modules without a dedicated mobile
    // screen land on the management hub.
    finance: () => ({ route: 'ManagementFinanceScreen' }),
    bank: () => ({ route: 'ManagementFinanceScreen' }),
    invoices: () => ({ route: 'ManagementFinanceScreen' }),
    expenses: () => ({ route: 'ManagementFinanceScreen' }),
    sales: () => ({ route: 'SalesDashboardScreen' }),
    crm: () => ({ route: 'ManagementCrmScreen' }),
    quotes: () => ({ route: 'ManagementCrmScreen' }),
    workorders: () => ({ route: 'WorkSheetListScreen' }),
    support: () => ({ route: 'ManagementListScreen', params: { type: 'supportTickets' } }),
    stock: () => ({ route: 'WarehousemanHomeScreen' }),
    projects: () => ({ route: 'ManagementHomeScreen' }),
    procurement: () => ({ route: 'ProcurementHomeScreen' }),
    hr: () => ({ route: 'ManagementHomeScreen' }),
    marketing: () => ({ route: 'ManagementHomeScreen' }),
    fleet: () => ({ route: 'ManagementFleetStatsScreen' }),
};

const MAX_ATTEMPTS = 20;
const RETRY_MS = 250;

/**
 * Run the action carried by a widget deep link (`?action=<verb>`), then republish the widget snapshot so the home
 * screen repaints from the server truth. This is how the iOS widgets act when the extension ships WITHOUT App Intents
 * (the default build: buttons are links) - the tap opens the app, which performs the verb through the same
 * `widgetActions` the Android widgets use. Never throws; unknown verbs are ignored.
 * @param {string} target Widget target segment (`timer` | `order`).
 * @param {Record<string,string>} params Parsed query params.
 * @returns {Promise<void>}
 */
const runLinkAction = async (target, params) => {
    const action = params && params.action;
    if (!action) {
        return;
    }

    try {
        /* eslint-disable global-require */
        const actions = require('./widgetActions');
        /* eslint-enable global-require */
        const workday = params.clock === 'workday';

        if (target === 'order') {
            if (action === 'accept') {
                await actions.acceptOrder(params.id);
            } else if (action === 'reject') {
                await actions.rejectOrder(params.id);
            }
        } else if (target === 'timer') {
            const table = workday
                ? {
                    pause: actions.pauseWorkdayClock,
                    resume: actions.resumeWorkdayClock,
                    resumeWork: actions.resumeWorkdayClock,
                    break: actions.resumeWorkdayClock,
                    stop: actions.stopWorkdayClock,
                    start: actions.startWorkdayClock,
                }
                : {
                    pause: actions.pauseTimer,
                    resume: actions.resumeTimer,
                    break: actions.breakTimer,
                    stop: actions.stopTimer,
                    resumeWork: () => actions.startTimer({ kind: params.kind, id: params.id, taskId: params.taskId }),
                    start: () => actions.startTimer({ kind: params.kind, id: params.id, taskId: params.taskId }),
                };
            const run = table[action];
            if (typeof run === 'function') {
                await run();
            }
        }

        // eslint-disable-next-line global-require
        const { refreshWidgets } = require('./widgetRefresh');
        if (typeof refreshWidgets === 'function') {
            await refreshWidgets();
        }
    } catch (_error) {
        // A failed action must never break the navigation that follows.
    }
};

/**
 * Parse the widget target segment + query params out of a deep-link URL.
 * @param {string} url e.g. `happyapp://widget/mail?messageId=abc&threadKey=x`
 * @returns {{ target: string, params: Record<string,string> }|null}
 */
const parseTarget = (url) => {
    if (typeof url !== 'string' || !url.includes(`${WIDGET_DEEPLINK_SCHEME}://widget/`)) {
        return null;
    }

    const afterHost = url.split('://widget/')[1] || '';
    const target = afterHost.split(/[/?#]/)[0] || null;
    if (!target) {
        return null;
    }

    const params = {};
    const queryIndex = afterHost.indexOf('?');
    if (queryIndex !== -1) {
        afterHost.slice(queryIndex + 1).split('&').forEach((pair) => {
            if (!pair) {
                return;
            }
            const [rawKey, rawValue = ''] = pair.split('=');
            try {
                params[decodeURIComponent(rawKey)] = decodeURIComponent(rawValue);
            } catch (_error) {
                params[rawKey] = rawValue;
            }
        });
    }

    return { target, params };
};

/**
 * Handle a (possibly widget) deep-link URL. Safe to call with any URL — non-widget
 * URLs return false so the app's other link handlers can take over.
 * @param {string} url
 * @returns {boolean} True when the URL was a widget link (handled or queued).
 */
export const handleWidgetUrl = (url) => {
    const parsed = parseTarget(url);
    if (!parsed) {
        return false;
    }

    // Fire-and-forget: the action runs in the background while the screen opens.
    runLinkAction(parsed.target, parsed.params);

    const resolver = TARGET_ROUTES[parsed.target];
    const mapping = typeof resolver === 'function' ? resolver(parsed.params) : null;
    if (!mapping || !mapping.route) {
        // Recognized widget link with no specific route — app opens at default.
        return true;
    }

    let attempts = 0;
    const tryNavigate = () => {
        attempts += 1;
        const done = navigateWhenReady(mapping.route, mapping.params);
        if (!done && attempts < MAX_ATTEMPTS) {
            setTimeout(tryNavigate, RETRY_MS);
        }
    };

    tryNavigate();

    return true;
};

export default handleWidgetUrl;
